import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useMessage } from '../contexts/MessageContext';
import { User, Wallet, Settings, Activity, TrendingUp, DollarSign, Target, Award, Edit3, Save, X, Copy, Check } from 'lucide-react';
import BackButton from '../components/BackButton';

export default function UserProfile() {
  const { isLoggedIn, user, address } = useAuth();
  const { success, error } = useMessage();
  
  // 用户信息编辑状态
  const [isEditing, setIsEditing] = useState(false);
  const [userInfo, setUserInfo] = useState({
    username: user?.username || 'User',
    email: user?.email || ''
  });
  const [tempUserInfo, setTempUserInfo] = useState({ ...userInfo });
  
  // 复制状态
  const [isCopied, setIsCopied] = useState(false);

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-plypto-bg flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Access Denied</h1>
          <p className="text-gray-400">Please connect your wallet to view your profile.</p>
        </div>
      </div>
    );
  }

  const handleEdit = () => {
    setTempUserInfo({ ...userInfo });
    setIsEditing(true);
  };

  const handleSave = () => {
    setUserInfo({ ...tempUserInfo });
    setIsEditing(false);
    // TODO: 这里可以添加API调用来保存用户信息
    console.log('保存用户信息:', tempUserInfo);
  };

  const handleCancel = () => {
    setTempUserInfo({ ...userInfo });
    setIsEditing(false);
  };

  const handleInputChange = (field, value) => {
    setTempUserInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 复制地址到剪贴板
  const copyToClipboard = async () => {
    if (!address) return;
    
    try {
      await navigator.clipboard.writeText(address);
      setIsCopied(true);
      success('Address copied to clipboard');
      console.log('✅ Address copied to clipboard:', address);
      
      // 2秒后重置状态
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (err) {
      console.error('复制失败:', err);
      // 备用方案：使用旧的API
      try {
        const textArea = document.createElement('textarea');
        textArea.value = address;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setIsCopied(true);
        success('Address copied to clipboard');
        setTimeout(() => setIsCopied(false), 2000);
      } catch (fallbackErr) {
        console.error('备用复制方案也失败:', fallbackErr);
        error('Copy failed, please copy manually');
      }
    }
  };

  return (
    <div className="bg-plypto-bg min-h-screen">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 pt-6 pb-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <BackButton />
            <div className="h-6 w-px bg-white/20"></div>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">
                  {userInfo.username || 'User Profile'}
                </h1>
                <p className="text-gray-400">
                  {address ? `${address.slice(0, 8)}...${address.slice(-6)}` : 'No address'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          
          {/* Wallet Information */}
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <div className="flex items-center space-x-3 mb-4">
              <Wallet className="w-6 h-6 text-blue-400" />
              <h2 className="text-xl font-semibold text-white">Wallet Information</h2>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-gray-400">Wallet Address</label>
                <div className="relative">
                  <div className="text-white font-mono text-sm bg-white/5 rounded-lg p-3 pr-12 mt-1">
                    {address || 'Not connected'}
                  </div>
                  {address && (
                    <button
                      onClick={copyToClipboard}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 hover:bg-white/10 rounded-md transition-colors group"
                      title={isCopied ? 'Copied!' : 'Copy address'}
                    >
                      {isCopied ? (
                        <Check className="w-4 h-4 text-green-400" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-400 group-hover:text-white" />
                      )}
                    </button>
                  )}
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-400">Network</label>
                <div className="text-white text-sm bg-white/5 rounded-lg p-3 mt-1">
                  Ethereum Mainnet
                </div>
              </div>
            </div>
          </div>

          {/* User Information */}
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <Edit3 className="w-6 h-6 text-blue-400" />
                <h2 className="text-xl font-semibold text-white">User Information</h2>
              </div>
              {!isEditing ? (
                <button
                  onClick={handleEdit}
                  className="flex items-center space-x-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors text-white text-sm"
                >
                  <Edit3 className="w-4 h-4" />
                  <span>Edit</span>
                </button>
              ) : (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleSave}
                    className="flex items-center space-x-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 rounded-lg transition-colors text-white text-sm"
                  >
                    <Save className="w-4 h-4" />
                    <span>Save</span>
                  </button>
                  <button
                    onClick={handleCancel}
                    className="flex items-center space-x-1 px-3 py-1.5 bg-gray-600 hover:bg-gray-700 rounded-lg transition-colors text-white text-sm"
                  >
                    <X className="w-4 h-4" />
                    <span>Cancel</span>
                  </button>
                </div>
              )}
            </div>
            
            <div className="space-y-4">
              {/* Username */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Username
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={tempUserInfo.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    placeholder="Enter your username"
                  />
                ) : (
                  <div className="px-3 py-2 bg-white/5 rounded-lg text-white">
                    {userInfo.username || 'Not set'}
                  </div>
                )}
              </div>
              
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email
                </label>
                {isEditing ? (
                  <input
                    type="email"
                    value={tempUserInfo.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    placeholder="Enter your email"
                  />
                ) : (
                  <div className="px-3 py-2 bg-white/5 rounded-lg text-white">
                    {userInfo.email || 'Not set'}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Account Settings */}
          {/* <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <div className="flex items-center space-x-3 mb-4">
              <Settings className="w-6 h-6 text-purple-400" />
              <h2 className="text-xl font-semibold text-white">Account Settings</h2>
            </div>
            <div className="space-y-3">
              <button className="w-full text-left p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-white">
                Notification Preferences
              </button>
              <button className="w-full text-left p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-white">
                Privacy Settings
              </button>
              <button className="w-full text-left p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-white">
                Security Options
              </button>
            </div>
          </div> */}

          {/* Activity Overview */}
          <div className="bg-white/5 rounded-xl p-6 border border-white/10 md:col-span-2">
            <div className="flex items-center space-x-3 mb-4">
              <Activity className="w-6 h-6 text-green-400" />
              <h2 className="text-xl font-semibold text-white">Recent Activity</h2>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div>
                  <div className="text-white font-medium">Wallet Connected</div>
                  <div className="text-gray-400 text-sm">Connected to Plypto platform</div>
                </div>
                <div className="text-gray-400 text-sm">Just now</div>
              </div>
              <div className="text-center py-8 text-gray-400">
                <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No recent predictions or activities</p>
              </div>
            </div>
          </div>

          {/* Statistics */}
          <div className="bg-white/5 rounded-xl p-6 border border-white/10 md:col-span-2">
            <h2 className="text-xl font-semibold text-white mb-4">Statistics</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-400">0</div>
                <div className="text-gray-400 text-sm">Total Predictions</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-400">0</div>
                <div className="text-gray-400 text-sm">Successful</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-400">0%</div>
                <div className="text-gray-400 text-sm">Success Rate</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-400">$0</div>
                <div className="text-gray-400 text-sm">Total Earnings</div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
